/**
 * CloseDayModal — "Fechar Dia" single-tap flow.
 *
 * Combina 3 logs num só gesto:
 *   1) Nutrição — 1 tap num de 3 presets (Perfeito / Ligeiro / Falhei)
 *   2) Higiene — 3 toggles emoji (Sono / Água / Sais)
 *   3) Biomarcadores — só aparece se estiver "em janela" (>=6 dias desde último)
 *
 * Backend: chama /api/programs/{program}/log/{nutrition|hygiene|biomarker}
 * em paralelo.
 */

import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const NUTRITION_PRESETS = [
  { key: "perfect", label: "Perfeito", sub: "<20g carbs", color: "#84cc16",
    values: { carbs_g: 15, protein_g: 140, fat_g: 120, kcal: 1800 } },
  { key: "light", label: "Ligeiro", sub: "20-40g", color: "#facc15",
    values: { carbs_g: 30, protein_g: 130, fat_g: 100, kcal: 1700 } },
  { key: "off", label: "Falhei", sub: ">40g", color: "#f97316",
    values: { carbs_g: 60, protein_g: 110, fat_g: 80, kcal: 1600 } },
];

const HYGIENE_TOGGLES = [
  { key: "sleep_ok", label: "Dormi ≥7h", emoji: "😴" },
  { key: "water_ok", label: "Bebi ≥2.5L água", emoji: "💧" },
  { key: "electrolytes", label: "Tomei electrólitos", emoji: "⚡" },
];

function daysSince(iso) {
  if (!iso) return Infinity;
  try {
    const d = new Date(iso);
    return (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24);
  } catch { return Infinity; }
}

export default function CloseDayModal({ open, program, biomarkerLog = [], onClose, onSaved }) {
  const [preset, setPreset] = useState(null);
  const [toggles, setToggles] = useState({ sleep_ok: false, water_ok: false, electrolytes: false });
  const [weight, setWeight] = useState("");
  const [waist, setWaist] = useState("");
  const [showBio, setShowBio] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Detect if biomarker check-in is due (>=6 days since last)
  const lastBio = biomarkerLog?.length ? biomarkerLog[biomarkerLog.length - 1] : null;
  const bioDue = daysSince(lastBio?.date || lastBio?.logged_at) >= 6;

  useEffect(() => {
    if (open) {
      setPreset(null);
      setToggles({ sleep_ok: false, water_ok: false, electrolytes: false });
      setWeight("");
      setWaist("");
      setShowBio(bioDue);
      setSubmitting(false);
    }
  }, [open, bioDue]);

  if (!open) return null;

  const canSubmit = preset !== null; // nutrition preset is the minimum contract

  const handleSubmit = async () => {
    if (!canSubmit) {
      toast.error("Escolhe pelo menos como comeste hoje.");
      return;
    }
    setSubmitting(true);
    const presetObj = NUTRITION_PRESETS.find((p) => p.key === preset);

    const calls = [];

    // 1) Nutrição
    calls.push(fetch(`${API}/api/programs/${program}/log/nutrition`, {
      method: "POST", headers: authHeaders(), body: JSON.stringify(presetObj.values),
    }));

    // 2) Higiene — always send (toggle=false becomes lower values)
    const hygienePayload = {
      sleep_h: toggles.sleep_ok ? 7.5 : 6,
      water_l: toggles.water_ok ? 2.5 : 1.5,
      electrolytes: !!toggles.electrolytes,
    };
    calls.push(fetch(`${API}/api/programs/${program}/log/hygiene`, {
      method: "POST", headers: authHeaders(), body: JSON.stringify(hygienePayload),
    }));

    // 3) Biomarcadores — só se utilizador preencheu
    const w = parseFloat(weight);
    const wc = parseFloat(waist);
    if (showBio && (w > 0 || wc > 0)) {
      const bioPayload = {};
      if (w > 0) bioPayload.weight = w;
      if (wc > 0) bioPayload.waist_cm = wc;
      calls.push(fetch(`${API}/api/programs/${program}/log/biomarker`, {
        method: "POST", headers: authHeaders(), body: JSON.stringify(bioPayload),
      }));
    }

    try {
      const results = await Promise.allSettled(calls);
      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed === 0) {
        toast.success("🎯 Dia fechado. Bom trabalho.", { duration: 4000 });
        onSaved?.();
        onClose();
      } else {
        toast.error(`Falhou ${failed} registo${failed === 1 ? "" : "s"} — tenta novamente.`);
      }
    } catch {
      toast.error("Erro ao fechar dia.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/85 backdrop-blur-md z-50 flex items-end sm:items-center justify-center"
        onClick={onClose}
        data-testid="close-day-modal"
      >
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 26, stiffness: 260 }}
          className="w-full sm:max-w-md relative rounded-t-3xl sm:rounded-3xl overflow-y-auto max-h-[92vh]"
          onClick={(e) => e.stopPropagation()}
          style={{
            background: "linear-gradient(180deg, rgba(132,204,22,0.10) 0%, #09090b 45%)",
            border: "1px solid rgba(132,204,22,0.35)",
            boxShadow: "0 -20px 60px rgba(132,204,22,0.25), inset 0 1px 0 rgba(132,204,22,0.3)",
          }}
        >
          {/* Grip handle */}
          <div className="sm:hidden flex justify-center pt-3 pb-1">
            <div className="w-12 h-1 rounded-full bg-zinc-700" />
          </div>

          <div className="px-6 pt-5 pb-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-[10px] uppercase tracking-widest font-bold text-lime-400 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Rápido · 30 seg
                </p>
                <h3 className="text-2xl font-black text-zinc-100 leading-tight mt-0.5">Fechar o dia</h3>
                <p className="text-xs text-zinc-500 mt-1">3 toques e está feito.</p>
              </div>
              <button
                onClick={onClose}
                className="text-zinc-400 hover:text-zinc-100 p-1 -mt-1 -mr-1"
                data-testid="close-day-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Step 1 — Nutrition preset */}
            <div className="mb-5">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-5 h-5 rounded-full bg-lime-500 text-black text-xs font-black flex items-center justify-center">1</span>
                <p className="text-sm font-bold text-zinc-100">Como comeste hoje?</p>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {NUTRITION_PRESETS.map((p) => {
                  const active = preset === p.key;
                  return (
                    <button
                      key={p.key}
                      onClick={() => setPreset(p.key)}
                      className="p-3 rounded-xl border text-left transition-all active:scale-95"
                      style={{
                        borderColor: active ? p.color : "#27272a",
                        background: active ? `${p.color}22` : "rgba(24,24,27,0.7)",
                        boxShadow: active ? `0 0 20px ${p.color}50` : "none",
                      }}
                      data-testid={`close-day-nutrition-${p.key}`}
                    >
                      <div className="text-sm font-bold" style={{ color: p.color }}>{p.label}</div>
                      <div className="text-[10px] text-zinc-500 mt-0.5">{p.sub}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Step 2 — Hygiene toggles */}
            <div className="mb-5">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-5 h-5 rounded-full bg-cyan-500 text-black text-xs font-black flex items-center justify-center">2</span>
                <p className="text-sm font-bold text-zinc-100">Higiene do dia</p>
              </div>
              <div className="space-y-2">
                {HYGIENE_TOGGLES.map((t) => {
                  const on = !!toggles[t.key];
                  return (
                    <button
                      key={t.key}
                      onClick={() => setToggles({ ...toggles, [t.key]: !on })}
                      className="w-full flex items-center gap-3 p-3 rounded-xl border transition-all active:scale-[0.98]"
                      style={{
                        borderColor: on ? "#06b6d4" : "#27272a",
                        background: on ? "rgba(6,182,212,0.15)" : "rgba(24,24,27,0.7)",
                        boxShadow: on ? "0 0 18px rgba(6,182,212,0.35)" : "none",
                      }}
                      data-testid={`close-day-hygiene-${t.key}`}
                    >
                      <span className="text-2xl">{t.emoji}</span>
                      <span className="flex-1 text-left text-sm font-semibold text-zinc-100">{t.label}</span>
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center"
                        style={{
                          background: on ? "#06b6d4" : "transparent",
                          border: on ? "none" : "2px solid #3f3f46",
                        }}
                      >
                        {on && <Check className="w-4 h-4 text-black" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Step 3 — Biomarkers (weekly, optional) */}
            <div className="mb-5">
              <button
                onClick={() => setShowBio(!showBio)}
                className="w-full flex items-center justify-between gap-2 mb-2"
                data-testid="close-day-toggle-bio"
              >
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-emerald-500 text-black text-xs font-black flex items-center justify-center">3</span>
                  <p className="text-sm font-bold text-zinc-100">Biomarcadores</p>
                  {bioDue && (
                    <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300">
                      Semanal · devido
                    </span>
                  )}
                </div>
                <span className="text-[10px] text-zinc-500">{showBio ? "Ocultar" : "Adicionar"}</span>
              </button>
              {showBio && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1 block">Peso (kg)</label>
                    <Input
                      type="number" step="0.1" placeholder="78.5"
                      value={weight}
                      onChange={(e) => setWeight(e.target.value)}
                      className="bg-zinc-900 border-zinc-800 text-zinc-100 h-11 text-base"
                      data-testid="close-day-weight"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1 block">Cintura (cm)</label>
                    <Input
                      type="number" step="0.1" placeholder="82"
                      value={waist}
                      onChange={(e) => setWaist(e.target.value)}
                      className="bg-zinc-900 border-zinc-800 text-zinc-100 h-11 text-base"
                      data-testid="close-day-waist"
                    />
                  </div>
                </div>
              )}
            </div>

            <Button
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
              className="w-full h-12 text-base font-bold text-black disabled:opacity-40 disabled:cursor-not-allowed"
              style={{
                background: "linear-gradient(135deg, #84cc16, #10b981)",
                boxShadow: "0 8px 28px rgba(132,204,22,0.5)",
              }}
              data-testid="close-day-submit"
            >
              {submitting ? "A guardar..." : "🎯 Fechar Dia"}
            </Button>

            {!canSubmit && (
              <p className="text-[10px] text-zinc-500 text-center mt-2">Escolhe pelo menos o preset de nutrição.</p>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
